import NiceModal from '@ebay/nice-modal-react';
import { useTranslation } from 'react-i18next';

import { API_KEY_ERROR_CODES, getApiKeyErrorCode } from 'api/meedioBackend/apiKeys/getApiKeyErrorCode';
import { ReactComponent as List } from 'assets/icons/List.svg';
import SettingsCard from 'modules/SettingsModal/SettingsCard/SettingsCard';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';

import ApiKeysCreateRow from './ApiKeysCreateRow';
import ApiKeysDisabled from './ApiKeysDisabled';
import ApiKeysDocumentationRow from './ApiKeysDocumentationRow';
import ApiKeysList from './ApiKeysList';
import ApiKeysNoAccess from './ApiKeysNoAccess';
import useApiKeys from './useApiKeys';
import useExpiredApiKeys from './useExpiredApiKeys';
import useWorkspaceBackendUuid from './useWorkspaceBackendUuid';
import GenerateApiKeyModal from '../modals/GenerateApiKeyModal';

const ApiKeysTab = () => {
  const { t } = useTranslation();
  const { workspaceId } = useWorkspacesContext({ requireWorkspaceId: true });
  const { workspaceUuid } = useWorkspaceBackendUuid(workspaceId);

  const active = useApiKeys(workspaceUuid);
  const expired = useExpiredApiKeys(workspaceUuid);

  // The active list GET is the canonical tab-level gate: a 403 swaps the whole
  // card body for the no-access / disabled panel (no header, sub-tabs or rows).
  const activeErrorCode = getApiKeyErrorCode(active.error);

  // A revoke moves a key from Active to Expired, so both streams refresh.
  const handleKeysChanged = () => {
    active.refresh();
    expired.refresh();
  };

  const handleCreate = () => {
    if (!workspaceUuid) return;
    void NiceModal.show(GenerateApiKeyModal, { workspaceUuid, onCreated: active.refresh });
  };

  const renderBody = () => {
    if (activeErrorCode === API_KEY_ERROR_CODES.restApiDisabled) return <ApiKeysDisabled />;
    if (activeErrorCode === API_KEY_ERROR_CODES.insufficientPl) return <ApiKeysNoAccess />;

    return (
      <div className="flex h-full w-full flex-col">
        <SettingsCard.Header title={t('workspaces_settings.api_keys.list_heading')} icon={List} className="!px-4" />
        <SettingsCard.Content className="border-gray-30 border-b !px-4">
          <ApiKeysCreateRow onCreate={handleCreate} disabled={!workspaceUuid} />
        </SettingsCard.Content>
        <SettingsCard.Content className="!px-4">
          <ApiKeysDocumentationRow />
        </SettingsCard.Content>
        <SettingsCard.Content className="!px-4">
          <ApiKeysList
            active={active}
            expired={expired}
            isResolvingWorkspace={!workspaceUuid}
            workspaceUuid={workspaceUuid}
            onKeysChanged={handleKeysChanged}
          />
        </SettingsCard.Content>
      </div>
    );
  };

  return (
    <div className="flex flex-col space-y-4">
      <span className="text-xl font-semibold">{t('workspaces_settings.api_keys.tab_title')}</span>
      <SettingsCard>{renderBody()}</SettingsCard>
    </div>
  );
};

export default ApiKeysTab;
